"use client";

import { useSyncExternalStore } from "react";

export type InstallPlatform = "ios" | "android" | "other";

type InstallState = {
  /** False on the server and while hydrating, so nothing renders before mount. */
  ready: boolean;
  platform: InstallPlatform;
  /** iPhone/iPad Safari — the one iOS browser whose Share sheet offers "Add to Home Screen" for sure. */
  isIosSafari: boolean;
  /** Phone or tablet. A desktop window keeps a fine pointer however narrow it gets. */
  isTouchDevice: boolean;
  isInstalled: boolean;
  installEvent: BeforeInstallPromptEvent | null;
  guideOpen: boolean;
};

const INSTALLED_KEY = "deemceeAppInstalled";

const SERVER_STATE: InstallState = {
  ready: false,
  platform: "other",
  isIosSafari: false,
  isTouchDevice: false,
  isInstalled: false,
  installEvent: null,
  guideOpen: false,
};

// Module-level store: `beforeinstallprompt` fires once per page load, often
// before any menu mounts, so it is captured here when the module first loads
// (the guide is mounted in the root layout) and every hook reads the same copy.
let state: InstallState = SERVER_STATE;
const listeners = new Set<() => void>();

function setState(patch: Partial<InstallState>) {
  state = { ...state, ...patch };
  listeners.forEach((listener) => listener());
}

function isBeforeInstallPromptEvent(
  event: Event,
): event is BeforeInstallPromptEvent {
  return "prompt" in event && typeof event.prompt === "function";
}

function readInstalledFlag(): boolean {
  try {
    return window.localStorage.getItem(INSTALLED_KEY) === "1";
  } catch {
    // Storage blocked (private mode): fall back to the display-mode checks.
    return false;
  }
}

function markInstalled() {
  try {
    window.localStorage.setItem(INSTALLED_KEY, "1");
  } catch {
    // Storage blocked: the in-memory flag below still hides the row this visit.
  }
  setState({ isInstalled: true, installEvent: null, guideOpen: false });
}

function onMediaChange(
  query: MediaQueryList,
  handler: (event: MediaQueryListEvent) => void,
) {
  // Safari < 14 only has the legacy addListener; a throw here would take the menu down with it.
  if (typeof query.addEventListener === "function") {
    query.addEventListener("change", handler);
  } else {
    query.addListener(handler);
  }
}

if (typeof window !== "undefined") {
  const { navigator } = window;
  const userAgent = navigator.userAgent;
  // iPadOS Safari reports a Mac user agent; touch points give it away.
  const isIos =
    /iPhone|iPad|iPod/i.test(userAgent) ||
    (/Macintosh/i.test(userAgent) && navigator.maxTouchPoints > 1);
  const coarsePointer = window.matchMedia("(pointer: coarse)");
  const standaloneDisplay = window.matchMedia("(display-mode: standalone)");
  const iosStandalone =
    "standalone" in navigator && navigator.standalone === true;

  state = {
    ready: true,
    platform: isIos ? "ios" : /Android/i.test(userAgent) ? "android" : "other",
    // Chrome/Firefox/Edge/Google app and in-app browsers on iOS also say "Safari".
    isIosSafari:
      isIos &&
      /Safari/i.test(userAgent) &&
      !/CriOS|FxiOS|EdgiOS|OPiOS|GSA|FBAN|FBAV|Instagram|Line\//i.test(userAgent),
    isTouchDevice: coarsePointer.matches,
    isInstalled:
      iosStandalone || standaloneDisplay.matches || readInstalledFlag(),
    installEvent: null,
    guideOpen: false,
  };

  onMediaChange(coarsePointer, (event) =>
    setState({ isTouchDevice: event.matches }),
  );
  onMediaChange(standaloneDisplay, (event) => {
    if (event.matches) setState({ isInstalled: true });
  });

  // No preventDefault: Chrome keeps its own install hint exactly as before;
  // we only keep the event so the menu row can open the native prompt.
  window.addEventListener("beforeinstallprompt", (event) => {
    if (isBeforeInstallPromptEvent(event)) setState({ installEvent: event });
  });
  window.addEventListener("appinstalled", markInstalled);
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

const getSnapshot = () => state;
const getServerSnapshot = () => SERVER_STATE;

function setGuideOpen(open: boolean) {
  setState({ guideOpen: open });
}

/** Native install prompt when the browser handed us one, otherwise the step-by-step guide. */
async function startInstall() {
  const installEvent = state.installEvent;
  if (!installEvent) {
    setGuideOpen(true);
    return;
  }
  try {
    // First statement, so prompt() still runs inside the tap's user activation.
    await installEvent.prompt();
    const choice = await installEvent.userChoice;
    if (choice.outcome === "accepted") {
      markInstalled();
      return;
    }
    // A prompt can be shown only once; the next tap opens the guide instead.
    setState({ installEvent: null });
  } catch {
    // Chrome refused the prompt (already used, or no user activation) — the guide still works.
    setState({ installEvent: null, guideOpen: true });
  }
}

export default function useInstallPrompt() {
  const snapshot = useSyncExternalStore(
    subscribe,
    getSnapshot,
    getServerSnapshot,
  );

  return {
    platform: snapshot.platform,
    isIosSafari: snapshot.isIosSafari,
    isInstalled: snapshot.isInstalled,
    /** Native one-tap install is available (Chromium fired beforeinstallprompt). */
    canPromptInstall: snapshot.installEvent !== null,
    /** Mounted, on a phone/tablet, and not installed — the only time the menu row shows. */
    canOfferInstall:
      snapshot.ready && snapshot.isTouchDevice && !snapshot.isInstalled,
    guideOpen: snapshot.guideOpen,
    setGuideOpen,
    startInstall,
  };
}
